import type { CurrentUser, Permission } from "@led-signage/shared/management-api";
import { createContext, useContext, type ReactNode } from "react";
import { api } from "./api/index.js";
import { useResource, type Resource } from "./api/useResource.js";

/**
 * いま操作している利用者。
 *
 * `permissions` は**画面の出し分けにだけ**使う。認可はサーバーが毎request
 * 判定する。ここで隠しても守りにはならず、押せない操作を押させないための配慮
 * である。したがって取得に失敗しても操作を止めない。
 */

const SessionContext = createContext<Resource<CurrentUser> | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useResource((signal) => api.getCurrentUser(signal), []);
  return (
    <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
  );
}

export function useSession(): Resource<CurrentUser> | null {
  return useContext(SessionContext);
}

/**
 * 権限を持つと分かっているか。
 *
 * 取得できていない間は `true` を返す。**分からないことを「できない」と
 * 表示すると、実際にはできる操作を止めてしまう**ため、サーバーの判断に委ねる。
 */
export function useCan(permission: Permission): boolean {
  const session = useContext(SessionContext);
  if (session === null || session.state !== "ready") return true;
  return session.value.permissions.includes(permission);
}

/** 権限が無いと分かっているときだけ理由を出す。 */
export function PermissionNotice({
  permission,
  action,
}: {
  permission: Permission;
  action: string;
}) {
  const allowed = useCan(permission);
  if (allowed) return null;
  return (
    <div className="inline-alert">
      <b>{action}の権限がありません</b>
      <span>
        この操作には <code>{permission}</code> が必要です。管理者へ依頼してください。
      </span>
    </div>
  );
}

export function CurrentUserBadge() {
  const session = useSession();
  if (session === null || session.state !== "ready") return null;
  const user = session.value;
  return (
    <span className="current-user" title={`${user.email} / ${user.roles.join(", ")}`}>
      <b>{user.displayName}</b>
      <small>{user.roles.join(" / ")}</small>
    </span>
  );
}
